import { AxiosResponse } from 'axios';
import { ContractingType } from 'v3/data/store/contracting/types';
import { IDevice } from 'v3/data/store/device/types';

import api from 'v3/services/config';
import {
  ICacheStoreIdsFilterRequest,
  IContractingEventsRequest,
  ICreateGroupAdminRequest,
  ICreateGroupsNameRequest,
  ICreateSellerRequest,
  ICreateStoreParams,
  IDisableSellerRequest,
  IGetAllStoresParams,
  IGetContractingListParams,
  IGetGroupsParams,
  IGetProductsParams,
  IGetSkuParams,
  IGetStoreIds,
  IGetUserAccountConfigRequest,
  IJazzUpdatePersonRequest,
  IListAllSellersRequest,
  IListGroupAdminsRequest,
  IListGroupProductsParams,
  ISellerRankingReportDataRequest,
  IUpdateGroupAdminRequest,
  IUpdateGroupProductRequest,
  IUpdateGroupsNameRequest,
  IUpdateLmiRequest,
  IUpdateProductRequest,
  IUpdatePurchaseInfoRequest,
  IUpdateSellerRequest,
  IUpdateSkuMaxPercentageRequest,
  IUpdateStoreNameAndGroupRequest,
  PixQRCodePaymentRequest,
} from 'v3/services/interfaces/request';
import {
  ICbbContractTerm,
  ICreateContractingEventsResponse,
  ICreateGroupsNameResponse,
  ICreateSellerResponse,
  ICreateStoreResponse,
  IDisableSellerResponse,
  IFindAllCatalogProductsResponse,
  IFindStoreGroupsResponse,
  IGetAccountResponse,
  IGetAllSkusResponse,
  IGetAllStoresResponse,
  IGetContractingEventsResponse,
  IGetContractingListResponse,
  IGetContractingListViewResponse,
  IGetContractingStateResponse,
  IGetDatasysVoucherResponse,
  IGetDevice,
  IGetEmailAvailability,
  IGetFinancingDetailsResponse,
  IGetFinancingResponse,
  IGetFlagStatusResponse,
  IGetGroupsResponse,
  IGetJazzStoreDataResponse,
  IGetPersonalInformationResponse,
  IGetProductResponse,
  IGetStoreDataResponse,
  IGetStores,
  IGetUserAccountConfigResponse,
  IInstallmentResponse,
  IListAllSellersResponse,
  IListGroupAdminsResponse,
  IListGroupCatalogProductsResponse,
  IListSellerResponse,
  ISellerRankingReportDataResponse,
  ISignContractResponse,
  IStatusResponse,
  ISuccessResponse,
  IUpdateGroupsNameResponse,
  IUpdateProductResponse,
  IUpdateSellerResponse,
  IUploadSellerCreationFileResponse,
  PixQRCodePaymentResponse,
} from 'v3/services/interfaces/response';
import { getStoreUserId } from 'v3/services/utils/get-store-user-id';
import { TokenFormatEnum } from 'v3/shared/constants/token-type.enum';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';
import { IGetPreferenceOptionsResponse } from './interfaces/response';

export const getContracting = (contractingKey: string): Promise<AxiosResponse<ContractingType>> => {
  return api.get(`/contracting/find-contracting/${contractingKey}`);
};

export const getContractingList = (
  body: IGetContractingListParams,
): Promise<AxiosResponse<IGetContractingListResponse>> => {
  return api.post(`/contracting/list`, body);
};

export const getContractingListView = (
  body: IGetContractingListParams,
): Promise<AxiosResponse<IGetContractingListViewResponse>> => {
  return api.post(`/contracting/list-view`, body);
};

export const generateToken = (contractingKey: string, tokenSendType: TokenFormatEnum): Promise<AxiosResponse<void>> => {
  return api.post(`/contracting/generate-token/${contractingKey}`, { tokenSendType });
};

export const validateToken = (contractingKey: string, token: string): Promise<AxiosResponse<IStatusResponse>> => {
  return api.post(`/contracting/validate-token/${contractingKey}`, { token });
};

export const getContractingState = (contractingKey: string): Promise<AxiosResponse<IGetContractingStateResponse>> => {
  return api.get(`/contracting/get-state/${contractingKey}`);
};

export const signContract = (contractingKey: string): Promise<AxiosResponse<ISignContractResponse>> => {
  return api.post(`/contracting/sign-contract/${contractingKey}`);
};

export const upsertDevice = (contractingKey: string, device: IDevice): Promise<AxiosResponse<IDevice>> => {
  return api.put(`/device/upsert-device/${contractingKey}`, device);
};

export const getProducts = (params: IGetProductsParams): Promise<AxiosResponse<IFindAllCatalogProductsResponse>> => {
  return api.get(`/product-v3/find-all`, { params });
};
export const listGroupProducts = (
  params: IListGroupProductsParams,
): Promise<AxiosResponse<IListGroupCatalogProductsResponse>> => {
  return api.get(`/product-v3/get-all-products`, { params });
};

export const getAllProductsSku = (params: IGetSkuParams): Promise<AxiosResponse<IGetAllSkusResponse>> => {
  return api.get('/product-v3/sku/find-all', { params });
};

export const updateProduct = (
  skuStoreId: string,
  body: IUpdateProductRequest,
): Promise<AxiosResponse<IUpdateProductResponse>> => {
  return api.put(`/motostore/product/${skuStoreId}`, { ...body });
};

export const updateGroupProduct = (
  skuId: string,
  body: IUpdateGroupProductRequest,
): Promise<AxiosResponse<{ count: number }>> => {
  return api.patch(`/product-v3/update-batch/${skuId}`, { ...body });
};

export const updateLmi = (body: IUpdateLmiRequest): Promise<AxiosResponse<IStatusResponse>> => {
  return api.patch(`/product-v3/update-lmi`, body);
};

export const getStoreUserAccount = (
  body: IGetUserAccountConfigRequest,
): Promise<AxiosResponse<IGetUserAccountConfigResponse>> => {
  return api.post('/backoffice/store/account', body);
};

export const getStoreData = (storeUserId: string): Promise<AxiosResponse<IGetStoreDataResponse>> => {
  return api.get(`/store-v3/find/${storeUserId}`);
};

export const getPersonalInformation = (
  contractingKey: string,
): Promise<AxiosResponse<IGetPersonalInformationResponse>> => {
  return api.get(`/person-v3/get-personal-information/${contractingKey}`);
};

export const getFinancing = (contractingKey: string): Promise<AxiosResponse<IGetFinancingResponse>> => {
  return api.get(`/financing-v3/find/${contractingKey}`);
};

export const getFinancingDetails = (contractingKey: string): Promise<AxiosResponse<IGetFinancingDetailsResponse>> => {
  return api.get(`/financing-details/find/${contractingKey}`);
};

export const getProduct = (contractingKey: string): Promise<AxiosResponse<IGetProductResponse>> => {
  return api.get(`/product-v3/find/${contractingKey}`);
};

export const getDevice = (contractingKey: string): Promise<AxiosResponse<IGetDevice>> => {
  return api.get(`/device/find/${contractingKey}`);
};

export const getStores = (params: IGetStoreIds): Promise<AxiosResponse<IGetStores[]>> => {
  return api.get(`/store-v3/find-list`, { params });
};

export const getAllStores = (params: IGetAllStoresParams): Promise<AxiosResponse<IGetAllStoresResponse>> => {
  return api.get('/store-v3/find-all', { params });
};

export const createStore = (params: ICreateStoreParams): Promise<AxiosResponse<ICreateStoreResponse>> => {
  return api.post('/store-v3/create-store', params);
};

export const getJazzStoreData = (jazzStoreId: string): Promise<AxiosResponse<IGetJazzStoreDataResponse>> => {
  return api.get(`/store-v3/jazz-store-data/${jazzStoreId}`);
};

export const jazzUpdatePerson = (
  contractingKey: string,
  data: IJazzUpdatePersonRequest,
): Promise<AxiosResponse<void>> => {
  return api.patch(`person-v3/jazz-update/${contractingKey}`, data);
};

export const getFlagStatus = async (flagName: FeatureFlagEnum): Promise<AxiosResponse<IGetFlagStatusResponse>> => {
  const storeUserId = await getStoreUserId();
  return api.get(`/feature-flag/${flagName}/${storeUserId}`);
};

export const verifyEmailAvailability = (
  email: string,
  hideLoading = true,
): Promise<AxiosResponse<IGetEmailAvailability>> => {
  return api.get(`/person-v3/verify-email-availability?email=${email}`, {
    headers: { 'x-bypass-loading': hideLoading },
  });
};

export const getAccount = (contractingKey: string): Promise<AxiosResponse<IGetAccountResponse>> => {
  return api.get(`/person-v3/get-account/${contractingKey}`);
};

export const findSeller = (code: string, storeId: string): Promise<AxiosResponse<IListSellerResponse>> => {
  return api.get(`/seller/${code}?storeId=${storeId}`);
};

export const createContractingEvents = (
  body: IContractingEventsRequest,
): Promise<AxiosResponse<ICreateContractingEventsResponse>> => {
  return api.post(`/contracting-events/create`, body);
};

export const getContractingEvents = (
  params: IContractingEventsRequest,
): Promise<AxiosResponse<IGetContractingEventsResponse[]>> => {
  return api.get(`/contracting-events/find`, { params });
};

export const listAllSellers = (body: IListAllSellersRequest): Promise<AxiosResponse<IListAllSellersResponse>> => {
  return api.post('/seller/list', body);
};

export const updateActiveSeller = (body: IDisableSellerRequest): Promise<AxiosResponse<IDisableSellerResponse>> => {
  return api.patch(`/seller/disable`, body);
};

export const createSeller = (body: ICreateSellerRequest): Promise<AxiosResponse<ICreateSellerResponse>> => {
  return api.post(`/seller/create`, body);
};

export const updateSeller = (
  sellerId: string,
  body: IUpdateSellerRequest,
): Promise<AxiosResponse<IUpdateSellerResponse>> => {
  return api.put(`/seller/${sellerId}`, body);
};

export const getAllStoreGroups = (): Promise<AxiosResponse<IFindStoreGroupsResponse[]>> => {
  return api.get('/store-group-v3/find-all');
};

export const updateStoreNameAndGroup = (
  storeUserId: string,
  body: IUpdateStoreNameAndGroupRequest,
): Promise<AxiosResponse<IStatusResponse>> => {
  return api.patch(`/store-v3/update-store-group-and-name/${storeUserId}`, body);
};

export const updateSkuMaxPercentage = (
  skuId: string,
  body: IUpdateSkuMaxPercentageRequest,
): Promise<AxiosResponse<ISuccessResponse>> => {
  return api.patch(`/product-v3/update-sku-max-percentage/${skuId}`, body);
};

export const uploadSellerCreationFile = (
  storeId: string,
  file: File,
): Promise<AxiosResponse<IUploadSellerCreationFileResponse>> => {
  const formData = new FormData();
  formData.append('file', file);

  return api.post(`/seller/create-by-file/${storeId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const getGroupList = (params: IGetGroupsParams): Promise<AxiosResponse<IGetGroupsResponse>> => {
  return api.get('/store-group-v3/list/', { params });
};

export const createGroup = (body: ICreateGroupsNameRequest): Promise<AxiosResponse<ICreateGroupsNameResponse>> => {
  return api.post(`/store-group-v3/create`, body);
};

export const updateGroupsName = (body: IUpdateGroupsNameRequest): Promise<AxiosResponse<IUpdateGroupsNameResponse>> => {
  return api.patch(`/store-group-v3/update-name`, body);
};

export const getGroupAdminList = (
  params: IListGroupAdminsRequest,
): Promise<AxiosResponse<IListGroupAdminsResponse>> => {
  const query = new URLSearchParams();

  if (params.name) query.append('name', params.name);
  if (params.page !== undefined) query.append('page', String(params.page));
  if (params.rowsPerPage !== undefined) query.append('rowsPerPage', String(params.rowsPerPage));

  params.storeGroupIds?.forEach((id) => {
    query.append('storeGroupIds', id);
  });

  return api.get(`/store-v3/group-admin-list/?${query.toString()}`);
};

export const updateGroupAdmin = (body: IUpdateGroupAdminRequest): Promise<AxiosResponse<void>> => {
  return api.patch(`/store-v3/edit-group-admin/`, body);
};

export const createGroupAdmin = (body: ICreateGroupAdminRequest): Promise<AxiosResponse<void>> => {
  return api.post(`/store-v3/create-group-admin/`, body);
};

export const updatePurchaseInfo = (
  body: IUpdatePurchaseInfoRequest,
  contractingKey: string,
): Promise<AxiosResponse<IDevice>> => {
  return api.patch(`/device/update-device/${contractingKey}`, body);
};
export const getCbbPreviewByContractingKey = (contractingKey: string): Promise<AxiosResponse<ICbbContractTerm>> => {
  return api.get(`/contracting/cbb-preview/${contractingKey}`);
};

export const getDashboardData = (params) => {
  return api.get(`/store-kpis`, { params });
};

export const cacheStoreIdsFilters = (
  sseToken: string,
  body: ICacheStoreIdsFilterRequest,
): Promise<AxiosResponse<void>> => {
  return api.post(`/contracting/generate-file-link/cache/${sseToken}`, body);
};

export const getPreferenceOptions = (): Promise<AxiosResponse<IGetPreferenceOptionsResponse[]>> => {
  return api.get(`/preference-options/find-by-store-user-id`);
};

export const getInstallments = (contractingKey: string): Promise<AxiosResponse<IInstallmentResponse>> => {
  return api.get(`contract-v3/payments/${contractingKey}/installments`);
};

export const generatePixQRCode = (
  contractingKey: string,
  body: PixQRCodePaymentRequest,
): Promise<AxiosResponse<PixQRCodePaymentResponse>> => {
  return api.post(`contract-v3/payments/pix/${contractingKey}`, body);
};

export const getSellerRankingData = (params) => {
  return api.get(`/seller/ranking-sellers`, { params });
};

export const getSellerRankingReportData = (
  params: ISellerRankingReportDataRequest,
): Promise<AxiosResponse<ISellerRankingReportDataResponse>> => {
  return api.get(`/seller/ranking-sellers-report`, {
    params,
  });
};

export const getSellerRankingReportStreamUrl = (jobId: string): string => {
  return `seller/ranking-sellers-report/stream/${jobId}`;
};

export const getVoucher = (contractingKey: string): Promise<AxiosResponse<IGetDatasysVoucherResponse>> => {
  return api.get(`/voucher/${contractingKey}`);
};
